import type { JSX, ReactNode } from "react";

import { formatDate } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    BubbleTag,
    renderMarkdown,
    StatusBadge,
    TagContainer,
} from "@impero-com/ui";

import type { LegacyControlProgramViewModelWithUserMap } from "api/legacy/model/legacyControlProgramViewModelWithUserMap";

import * as styles from "./MetadataPanel.css";
import { getTags } from "./utils";

interface MetadataPanelProps {
    model: LegacyControlProgramViewModelWithUserMap;
    userName: (userId: number | null | undefined) => string;
}

export function MetadataPanel({
    model,
    userName,
}: MetadataPanelProps): JSX.Element {
    const { t } = useTranslation();

    const tags = getTags(model.TagIds, model.ControlProgramTagCategories);

    return (
        <div className={styles.content}>
            <div className={styles.description}>
                {renderMarkdown(model.Description)}
            </div>
            <dl className={styles.properties}>
                <PropertyRow
                    label={t("ViewControlProgram_CreatedWhen")}
                    value={model.Created ? formatDate(model.Created) : ""}
                />
                <PropertyRow
                    label={t("ViewControlProgram_CreatedBy")}
                    value={userName(model.CreatedByUserId)}
                />
                <PropertyRow
                    label={t("ViewControlProgram_Status")}
                    value={
                        <StatusBadge
                            status={model.Running ? "success" : "default"}
                        >
                            {model.Running
                                ? t("ViewControlProgram_Status_Active")
                                : t("ViewControlProgram_Status_Inactive")}
                        </StatusBadge>
                    }
                />
                <PropertyRow
                    label={t("ViewControlProgram_Tags")}
                    value={
                        tags.length > 0 ? (
                            <TagContainer>
                                {tags.map((tag) => (
                                    <BubbleTag
                                        key={tag.id}
                                        category={tag.category}
                                        tag={tag.name}
                                    />
                                ))}
                            </TagContainer>
                        ) : (
                            "-"
                        )
                    }
                />
            </dl>
        </div>
    );
}

function PropertyRow({
    label,
    value,
}: {
    label: string;
    value: ReactNode;
}): JSX.Element {
    return (
        <div className={styles.propertyRow}>
            <dt className={styles.propertyLabel}>{label}</dt>
            <dd className={styles.propertyValue}>{value}</dd>
        </div>
    );
}
